import type { InferType } from 'yup';
import { formatCurrency } from '@/features/booking/booking-form.utils';
import {
  groupSizeLabel,
  toNumber,
} from '@/features/inventory/activity-pricing.utils';
import type { IAgeGroupPreset, IGroupSizePreset } from './price-preset.types';
import type {
  ageGroupPresetValidationSchema,
  groupSizePresetValidationSchema,
} from './price-preset.validations';

type AgeGroupPresetValues = InferType<typeof ageGroupPresetValidationSchema>;
type GroupSizePresetValues = InferType<typeof groupSizePresetValidationSchema>;
/** `companyId` rides the create only; the update DTO does not declare it. */
const toAgeGroupPresetPayload = (
  values: AgeGroupPresetValues,
  { create }: { create: boolean }
) => ({
  ...(create ? { companyId: values.companyId } : {}),
  label: values.label,
  defaultAmount: values.defaultAmount ?? 0,
  // Omitted on create = placed after the last preset.
  ...(values.order !== undefined ? { order: values.order } : {}),
  status: values.status,
});

const toGroupSizePresetPayload = (
  values: GroupSizePresetValues,
  { create }: { create: boolean }
) => ({
  ...(create ? { companyId: values.companyId } : {}),
  // `null` clears a stored name, so the range shows instead.
  label: values.label ? values.label : null,
  minParticipant: values.minParticipant,
  maxParticipant: values.maxParticipant ?? null,
  defaultAmount: values.defaultAmount ?? 0,
  status: values.status,
});

/** Picker and list order: `order` first (unset last), then by name. */
const sortAgeGroupPresets = (presets: IAgeGroupPreset[]) =>
  [...presets].sort(
    (a, b) =>
      (a.order ?? Infinity) - (b.order ?? Infinity) ||
      (a.label ?? '').localeCompare(b.label ?? '')
  );

const sortGroupSizePresets = (presets: IGroupSizePreset[]) =>
  [...presets].sort(
    (a, b) =>
      (toNumber(a.minParticipant) ?? 0) - (toNumber(b.minParticipant) ?? 0)
  );

const groupSizePresetLabel = (preset: IGroupSizePreset) =>
  preset.label?.trim() ||
  groupSizeLabel(preset.minParticipant, preset.maxParticipant);

/** -€ 5,00 for a discount, +€ 3,00 for a surcharge, € 0,00 for none. */
const formatSignedAmount = (amount: unknown) => {
  const value = toNumber(amount) ?? 0;
  if (value === 0) return formatCurrency(0);
  return `${value > 0 ? '+' : '-'}${formatCurrency(Math.abs(value))}`;
};

export {
  toAgeGroupPresetPayload,
  toGroupSizePresetPayload,
  sortAgeGroupPresets,
  sortGroupSizePresets,
  groupSizePresetLabel,
  formatSignedAmount,
};
export type { AgeGroupPresetValues, GroupSizePresetValues };
